import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Card, Group, Text, Button, SimpleGrid, Skeleton, FitText } from '@repletoryreach/sdk-ui';
import { PageHeader, StatusBadge, Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Sparkline } from '@repletoryreach/sdk-ui/charts';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import './health.css';

const MUTED = 'var(--rr-color-text-muted)';
const REFRESH_MS = 15_000;
/** How many probes each service's series keeps. Twelve minutes at 15 s. */
const HISTORY = 48;

const STATUS_KEY: Record<string, string> = {
  healthy: 'status.healthy',
  unhealthy: 'status.degraded',
  down: 'status.down'
};

const TONE_COLOUR = {
  healthy: 'var(--rr-color-success)',
  warning: 'var(--rr-color-warning)',
  down: 'var(--rr-color-error)',
  inactive: MUTED
} as const;

const RefreshIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 11a8 8 0 1 0-2.3 6.3" /><path d="M20 5v6h-6" />
  </svg>
);
const ServiceIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </svg>
);
const InfoIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.6v.4" />
  </svg>
);

function toneFor(status: string): 'healthy' | 'warning' | 'down' {
  if (status === 'healthy') return 'healthy';
  if (status === 'down') return 'down';
  return 'warning';
}

export default function HealthPage() {
  const t = useT();
  const nav = useNavigate();
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'ops', 'health'],
    queryFn: () => admin.listServiceHealth(),
    refetchInterval: REFRESH_MS
  });

  const rows = data?.data ?? [];
  const healthy = rows.filter((r) => r.status === 'healthy').length;
  const unhealthy = rows.filter((r) => r.status === 'unhealthy').length;
  const down = rows.filter((r) => r.status === 'down').length;
  const denied = isApiError(error) && error.status === 403;
  // Cards from an earlier poll stay on screen when a probe fails, so they have
  // to be marked as the last known result. Twelve green cards under a small
  // red banner otherwise reads as "everything is fine, ignore the banner".
  const stale = Boolean(error) && rows.length > 0;
  // Nothing cached and the probe failed: the counts below are unknown, not zero.
  const noResult = Boolean(error) && rows.length === 0;
  const degraded = !error && rows.length > 0 && down + unhealthy > 0;

  // The endpoint reports one probe, not a history, so the only trend anybody
  // can honestly draw is the one this page has watched since it was opened.
  // The series is decoration beside the figure it accompanies; the exact
  // millisecond value is always in the line above it.
  const history = useProbeHistory(rows, dataUpdatedAt);

  const stat = (n: number) => (noResult ? t('common.notAvailable') : formatInteger(n));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('system.title')}
        title={t('system.health.title')}
        description={t('system.health.description')}
        actions={
          <Group gap="sm" align="center">
            <Text role="caption" c={MUTED}>
              {dataUpdatedAt > 0
                ? t('system.health.lastChecked', { when: formatRelative(new Date(dataUpdatedAt).toISOString()) })
                : t('system.health.neverChecked')}
            </Text>
            <Button size="sm" leftSection={RefreshIcon} onClick={() => refetch()} loading={isFetching}>
              {t('action.refresh')}
            </Button>
          </Group>
        }
      />

      {/* The condition of the whole fleet, in one strip. It is four figures
          rather than four cards because the twelve service tiles below are the
          page, and a full card row pushed them under the fold. */}
      <div className="rr-health-summary">
        <SummaryCell tone="healthy" label={t('status.healthy')} value={stat(healthy)} loading={isLoading && rows.length === 0} />
        <SummaryCell tone="warning" label={t('status.degraded')} value={stat(unhealthy)} loading={isLoading && rows.length === 0} />
        <SummaryCell tone="down" label={t('status.down')} value={stat(down)} loading={isLoading && rows.length === 0} />
        <SummaryCell tone="inactive" label={t('common.total')} value={stat(rows.length)} loading={isLoading && rows.length === 0} />
      </div>

      {denied && (
        <Alert color="warning" title={t('system.health.denied.title')}>{t('system.health.denied.body')}</Alert>
      )}

      {error && !denied && (
        <Alert color="error" title={t('system.health.loadError')}>
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>
              {stale
                ? t('system.health.showingStale', {
                    when: dataUpdatedAt > 0 ? formatDateTime(new Date(dataUpdatedAt).toISOString()) : t('common.never')
                  })
                : t('system.health.noResult')}
            </span>
            <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      {degraded && (
        <Alert color="warning" title={t('system.health.banner.title')}>
          <Group justify="space-between" align="center" gap="md" wrap="wrap" style={{ width: '100%' }}>
            <span>{t('system.health.banner.body', { down, degraded: unhealthy, total: rows.length })}</span>
            {/* The console cannot list firing alert rules, so this leads to the
                page that can actually reach a person rather than pretending to
                open an incident queue that does not exist. */}
            <Button size="xs" variant="outline" onClick={() => nav('/system/alerts')}>
              {t('system.health.banner.action')}
            </Button>
          </Group>
        </Alert>
      )}

      {isLoading && rows.length === 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="md">
          {Array.from({ length: 10 }, (_, i) => (
            <Card key={i} withBorder>
              <Stack gap="xs">
                <Skeleton height={16} width="60%" />
                <Skeleton height={12} width="40%" />
                <Skeleton height={30} />
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Card withBorder>
          <EmptyState title={t('system.health.empty')} description={t('system.health.emptyHint')} />
        </Card>
      )}

      {rows.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="md" style={stale ? { opacity: 0.55 } : undefined}>
          {rows.map((r) => {
            const tone = toneFor(r.status);
            const colour = TONE_COLOUR[tone];
            const series = history[r.service] ?? [];
            return (
              <Card key={r.service} withBorder>
                <Stack gap="xs">
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Group gap="sm" align="center" wrap="nowrap">
                      <span
                        className="rr-health-service__icon"
                        style={{ color: colour, background: `color-mix(in srgb, ${colour} 12%, transparent)` }}
                        aria-hidden
                      >
                        {ServiceIcon}
                      </span>
                      {/* The service name is the name the logs, the queues and
                          the alert payloads all use, so it is left exactly as
                          the probe reports it. */}
                      <Text role="body" fw={600} lineClamp={1} title={r.service}>{r.service}</Text>
                    </Group>
                    <StatusBadge status={tone} size="xs">
                      {t(STATUS_KEY[r.status] ?? 'status.unknown')}
                    </StatusBadge>
                  </Group>
                  <Text role="caption" c={MUTED}>
                    {/* A `down` service reports http_status 0, which used to
                        render as "HTTP :" and read like a missing value rather
                        than a probe that never got an answer. */}
                    {r.http_status
                      ? t('system.health.probeResult', { status: r.http_status, ms: r.duration_ms })
                      : t('system.health.noResponse', { ms: r.duration_ms })}
                  </Text>
                  <div className="rr-health-service__spark" title={t('system.health.sparkLabel')}>
                    {series.length > 1 && (
                      <Sparkline data={series} color={colour} h={34} w="100%" />
                    )}
                  </div>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <Group gap="sm" align="center">
        <span style={{ color: MUTED, display: 'inline-flex' }}>{InfoIcon}</span>
        <Text role="caption" c={MUTED}>
          {t('system.health.footer.interval', { seconds: REFRESH_MS / 1000 })}
        </Text>
        <Text role="caption" c={MUTED} aria-hidden>·</Text>
        <Text role="caption" c={MUTED}>
          {stale || noResult ? t('system.health.footer.stale') : t('system.health.footer.live')}
        </Text>
      </Group>
    </Stack>
  );
}

/**
 * Probe durations per service, accumulated across polls.
 *
 * There is no server-side history behind `/v1/admin/ops/health`: each call
 * returns one probe. Keeping the values this page has seen is the only trend
 * it can draw without inventing one, and it resets when the page unmounts,
 * which is why the tiles say nothing about it beyond the tooltip.
 */
function useProbeHistory(rows: admin.ServiceHealthRow[], updatedAt: number): Record<string, number[]> {
  const [history, setHistory] = useState<Record<string, number[]>>({});

  useEffect(() => {
    if (updatedAt === 0 || rows.length === 0) return;
    setHistory((prev) => {
      const next: Record<string, number[]> = { ...prev };
      for (const r of rows) {
        next[r.service] = [...(prev[r.service] ?? []), r.duration_ms].slice(-HISTORY);
      }
      return next;
    });
    // The poll's timestamp is the event: `rows` is a fresh array on every
    // render, so depending on it would append the same probe repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt]);

  return history;
}

function SummaryCell({ tone, label, value, loading }: {
  tone: keyof typeof TONE_COLOUR;
  label: string;
  value: string;
  loading: boolean;
}) {
  const status = tone === 'inactive' ? 'inactive' : tone;
  return (
    <div className="rr-health-summary__cell">
      <StatusBadge status={status} size="sm">{label}</StatusBadge>
      {loading
        ? <Skeleton height={26} width={40} />
        : <Text role="display" fw={700} c={tone === 'inactive' ? undefined : TONE_COLOUR[tone]}><FitText>{value}</FitText></Text>}
    </div>
  );
}
